import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RecurringServices = ({ subscriptions, onUpdateSubscription, onCancelSubscription }) => {
  const [editingSubscription, setEditingSubscription] = useState(null);

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'bi-weekly', label: 'Bi-weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10 border-success/20';
      case 'paused':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'cancelled':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-text-secondary bg-muted border-border';
    }
  };

  const formatNextService = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleFrequencyChange = (subscriptionId, newFrequency) => {
    onUpdateSubscription(subscriptionId, { frequency: newFrequency });
    setEditingSubscription(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-elevation-2 border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">
            Recurring Services
          </h2>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
          >
            Add Subscription
          </Button>
        </div>
      </div>
      <div className="p-6">
        {subscriptions?.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="RefreshCw" size={32} color="var(--color-text-secondary)" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">
              No recurring services
            </h3>
            <p className="text-text-secondary mb-6">
              Set up automatic scheduling for regular cleaning services
            </p>
            <Button variant="default" iconName="Plus" iconPosition="left">
              Create Subscription
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {subscriptions?.map((subscription) => (
              <div
                key={subscription?.id}
                className="border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-text-primary">
                        {subscription?.service}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(subscription?.status)}`}>
                        {subscription?.status?.charAt(0)?.toUpperCase() + subscription?.status?.slice(1)}
                      </span>
                    </div>
                    <div className="text-sm text-text-secondary mb-2">
                      {subscription?.location}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-text-primary">
                      ${subscription?.price}
                    </div>
                    <div className="text-sm text-text-secondary">
                      per service
                    </div>
                  </div>
                </div>

                {/* Subscription Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-3 bg-muted rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-text-primary mb-1">
                      Frequency
                    </div>
                    {editingSubscription === subscription?.id ? (
                      <div className="flex items-center space-x-2">
                        <Select
                          options={frequencyOptions}
                          value={subscription?.frequency}
                          onChange={(value) => handleFrequencyChange(subscription?.id, value)}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="X"
                          onClick={() => setEditingSubscription(null)}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-text-secondary capitalize">
                          {subscription?.frequency}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit"
                          onClick={() => setEditingSubscription(subscription?.id)}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-text-primary mb-1">
                      Next Service
                    </div>
                    <div className="text-sm text-text-secondary">
                      {formatNextService(subscription?.nextService)}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-text-primary mb-1">
                      Services Completed
                    </div>
                    <div className="text-sm text-text-secondary">
                      {subscription?.completedServices} services
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="flex items-center justify-between mb-4 p-3 bg-surface border border-border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center">
                      <Icon name="CreditCard" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-text-primary">
                        {subscription?.paymentMethod?.type} ending in {subscription?.paymentMethod?.last4}
                      </div>
                      <div className="text-xs text-text-secondary">
                        Auto-renewal {subscription?.autoRenewal ? 'enabled' : 'disabled'}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    iconPosition="left"
                  >
                    Update
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-2">
                    {subscription?.status === 'active' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Pause"
                        iconPosition="left"
                        onClick={() => onUpdateSubscription(subscription?.id, { status: 'paused' })}
                      >
                        Pause
                      </Button>
                    ) : subscription?.status === 'paused' ? (
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Play"
                        iconPosition="left"
                        onClick={() => onUpdateSubscription(subscription?.id, { status: 'active' })}
                      >
                        Resume
                      </Button>
                    ) : null}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => onCancelSubscription(subscription?.id)}
                      className="text-error hover:text-error"
                    >
                      Cancel
                    </Button>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Schedule Now
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      iconPosition="left"
                    >
                      View History
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecurringServices;